import React, { useState, useEffect } from "react";
import {
  Drawer,
  Descriptions,
  Button,
  Tooltip,
  List,
  Input,
  Form,
  Spin,
  Space,
  Upload,
  message,
  Select,
  Modal
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  CheckOutlined,
  CloseOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
  MessageTwoTone,
  UploadOutlined,
  EditOutlined,
} from "@ant-design/icons";
import moment from "moment";
import classNames from "classnames";
import { getRole, getIsApprover } from "../../helpers";
import { useEditSalesRequestMutation } from "../../appRedux/api/royaltySalesApi";
import { royalty_countries_product } from "../../constants";

const { Option } = Select;

const { TextArea } = Input;
const confirm = Modal.confirm;

const RoyaltySalesDrawer = ({
  visible,
  onClose,
  requestData,
  isLoading,
  refetchData,
}) => {
  const [isAdminApprover, setIsAdminApprover] = useState(false);
  const [isUserAdmin, setIsUserAdmin] = useState(false);
  const [isUserPartner, setIsUserPartner] = useState(false);
  const [editSalesRequest, { isLoading: isEditing }] =
    useEditSalesRequestMutation();
  const [form] = Form.useForm();
  const [chatConversation, setChatConversation] = useState([]);
  const [isChatModalVisible, setIsChatModalVisible] = useState(false);
  const [messageInput, setMessageInput] = useState("");
  const currentDate = new Date();
  const [editMode, setEditMode] = useState({
    country: false,
    product: false,
    quantity: false
  });
  const [products, setProducts] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantityValue, setQuantityValue] = useState('');

  useEffect(() => {
    setIsAdminApprover(getIsApprover());
    setIsUserAdmin(getRole() === "ADMIN");
    setIsUserPartner(getRole() === "PARTNER");
  }, []);

  useEffect(() => {
    if (requestData?.country_id) {
      setSelectedCountry(requestData.country_id.country_id);
      setSelectedProduct(requestData.product_id.product_id);
      setQuantityValue(requestData.quantity);

      const country = royalty_countries_product.find(
        c => c.country_id === requestData.country_id.country_id
      );
      if (country) {
        setProducts(country.product);
      }
    }
  }, [requestData]);

  const handleCountryChange = (value) => {
    setSelectedCountry(value);
    setSelectedProduct(undefined);

    const selectedCountryData = royalty_countries_product.find(
      country => country.country_id === value
    );
    if (selectedCountryData) {
      setProducts(selectedCountryData.product);
    } else {
      setProducts([]);
    }
  };

  const saveField = async (field, value) => {
    const updateFormData = new FormData();
    updateFormData.append(field, value);

    try {
      await editSalesRequest({
        id: requestData.request_id,
        data: updateFormData,
        status: "Updated"
      });
      
      if (refetchData) refetchData();
      message.success(`${field.replace('_id', '')} updated successfully!`);
      setEditMode({...editMode, [field.replace('_id', '')]: false});
    } catch (error) {
      message.error("Failed to update field");
    }
  };

  const handleMessageChange = (e) => {
    setMessageInput(e.target.value);
  };

  const handleSendMessage = (item) => {
    if (messageInput.trim() !== "") {
      const newMessage = {
        message: messageInput.trim(),
        is_partner: isUserPartner,
      };
      const formData = new FormData();
      formData.append("status", "NEEDS CLARIFICATION");
      formData.append("message", newMessage.message);

      editSalesRequest({
        id: item,
        data: formData,
        status: "Message Sent",
      }).then(() => {
        if (refetchData) refetchData();
      });

      const updatedChatConversation = [...chatConversation, newMessage];
      setMessageInput("");
      setChatConversation(updatedChatConversation);
    }
  };

  const handleClarificationClick = (chatMessages) => {
    setChatConversation(chatMessages);
    setIsChatModalVisible(true);
  };

  const isDisabled = !isAdminApprover || 
    (requestData?.status !== "PENDING" &&
    requestData?.status !== "NEEDS CLARIFICATION");

  const isEditable = (status) => {
    return isAdminApprover && (
      (status === "APPROVED") &&
      !requestData?.is_sales_completed &&
      (status !== "APPROVED" || (
        !(requestData?.sales_data?.length > 0 &&
        requestData?.quantity) ||
        requestData?.sales_data.reduce(
          (total, item) => total + parseInt(item.quantity), 0
        ) < requestData?.quantity
      ))
    );
  };

  // Add a function to check if the user can reject based on status
  const canReject = () => {
    // If the request is already rejected, don't allow rejecting again
    if (requestData?.status === "REJECTED") return false;

    // If sales data exists and has quantity > 0, don't allow rejecting
    if (requestData?.sales_data?.length > 0 &&
        requestData?.sales_data.reduce((total, item) => total + parseInt(item.quantity), 0) > 0) {
      return false;
    }

    // For PENDING, NEEDS CLARIFICATION, or REVISIONS REQUIRED statuses, only Super admin can reject
    if (["PENDING", "NEEDS CLARIFICATION", "REVISIONS REQUIRED"].includes(requestData?.status)) {
      return isAdminApprover;
    }

    // For APPROVED status, only admin approvers can reject
    if (requestData?.status === "APPROVED") {
      return isAdminApprover;
    }

    // For other statuses, don't allow rejecting
    return false;
  };

  const showCommentsForm = () => {
    return (
      <Form form={form} layout="vertical" className="mt-20">
        <div className="mb-10">
          <span>Add comments for partner:</span>
        </div>
        <Form.Item
          name="comment"
          className="mb-0"
          rules={[
            {
              required: isUserAdmin,
              message: isUserAdmin
                ? "Enter comments for partner"
                : "Enter comments for admin",
            },
          ]}
        >
          <TextArea placeholder="Enter comments here" />
        </Form.Item>
      </Form>
    );
  };

  const showApproveConfirm = (item, partner_id) => {
    confirm({
      title: "Do you want to approve the report?",
      className: "btn-confirm-green",
      content: showCommentsForm(),
      icon: <CheckCircleOutlined />,
      okText: "Approve",
      cancelText: "Cancel",
      onOk(close) {
        form.validateFields().then((values) => {
          const approveFormData = new FormData();
          approveFormData.append("is_approved", true);
          approveFormData.append("status", "APPROVED");
          approveFormData.append("approval_date", currentDate.toISOString());
          approveFormData.append("comment", values.comment);
          approveFormData.append("partner_id", parseInt(partner_id));

          editSalesRequest({
            id: item,
            data: approveFormData,
            status: "Approved",
          }).then(() => {
            if (refetchData) refetchData();
            onClose();
          });

          close();
        });
      },
      onCancel() {
        form.resetFields(["comment"]);
      },
    });
  };

  const showRejectForm = () => {
    return (
      <Form form={form} layout="vertical" className="mt-20">
        <div className="mb-10">
          <span>Add comments for partner:</span>
        </div>
        <Form.Item
          name="comment"
          className="mb-10"
          rules={[{ required: true, message: "Enter comments for partner" }]}
        >
          <TextArea placeholder="Enter comments here" />
        </Form.Item>
        <div className="mb-10">
          <span>Upload rejection documents:</span>
        </div>
        <Form.Item
          name="rejection_docs"
          valuePropName="fileList"
          getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
        >
          <Upload
            beforeUpload={(file) => {
              const isValidType = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'].includes(file.type);
              const isValidSize = file.size / 1024 / 1024 <= 15;

              if (!isValidType) {
                message.error('Please upload a PDF, JPEG, or PNG document!');
                return Upload.LIST_IGNORE;
              }
              if (!isValidSize) {
                message.error('File size must be less than or equal to 15 MB!');
                return Upload.LIST_IGNORE;
              }

              const files = form.getFieldValue('rejection_docs') || [];
              form.setFieldsValue({ rejection_docs: [...files, file] });
              return false;
            }}
            onRemove={(file) => {
              const files = form.getFieldValue('rejection_docs') || [];
              form.setFieldsValue({ 
                rejection_docs: files.filter(f => f.uid !== file.uid) 
              });
            }}
            accept=".pdf,.jpg,.jpeg,.png"
          >
            <Button icon={<UploadOutlined />}>Select File</Button>
          </Upload>
        </Form.Item>
      </Form>
    );
  };

  const showRejectConfirm = (item, partner_id) => {
    form.resetFields(['comment', 'rejection_docs']);

    confirm({
      title: "Do you want to reject this Request?",
      className: "btn-confirm-red",
      content: showRejectForm(),
      okText: "Reject",
      cancelText: "Cancel",
      icon: <CloseCircleOutlined />,
      onOk(close) {
        form.validateFields().then(values => {
          const rejectFormData = new FormData();
          rejectFormData.append("status", "REJECTED");
          rejectFormData.append("comment", values.comment);
          rejectFormData.append("partner_id", parseInt(partner_id));
          rejectFormData.append("is_approved", false);

          // Append rejection docs with category
          values.rejection_docs?.forEach(file => {
            const fileWithCategory = new File([file.originFileObj], file.name, { 
              type: file.type 
            });
            fileWithCategory.category = 'REJECTED';
            rejectFormData.append("file", fileWithCategory);
          });

          editSalesRequest({
            id: item,
            data: rejectFormData,
            status: "Rejected",
          }).then(() => {
            if (refetchData) refetchData();
            onClose();
          });

          close();
        });
      },
      onCancel() {
        form.resetFields();
      },
    });
  };

  const showReviseConfirm = (item, partner_id) => {
    confirm({
      title: "Do you want to request for revisions?",
      className: "btn-confirm-primary",
      content: showCommentsForm(),
      okText: "Submit",
      cancelText: "Cancel",
      icon: <ExclamationCircleOutlined />,
      onOk(close) {
        form.validateFields().then((values) => {
          const rejectFormData = new FormData();
          rejectFormData.append("status", "REVISIONS REQUIRED");
          rejectFormData.append("comment", values.comment);
          rejectFormData.append("partner_id", parseInt(partner_id));
          rejectFormData.append("is_approved", false);

          editSalesRequest({
            id: item,
            data: rejectFormData,
            status: "Status Sent",
          }).then(() => {
            if (refetchData) refetchData();
            onClose();
          });

          close();
        });
      },
      onCancel() {
        form.resetFields(["comment"]);
      },
    });
  };

  const showClarificationConfirm = (item, partner_id) => {
    confirm({
      title: "Do you want to request for clarification?",
      className: "btn-confirm-primary",
      content: showCommentsForm(),
      okText: "Submit",
      cancelText: "Cancel",
      icon: <WarningOutlined />,
      onOk(close) {
        form.validateFields().then((values) => {
          const rejectFormData = new FormData();
          rejectFormData.append("status", "NEEDS CLARIFICATION");
          rejectFormData.append("message", values.comment);
          rejectFormData.append("partner_id", parseInt(partner_id));
          rejectFormData.append("is_approved", false);

          editSalesRequest({
            id: item,
            data: rejectFormData,
            status: "Status Sent",
          }).then(() => {
            if (refetchData) refetchData();
            onClose();
          });

          close();
        });
      },
      onCancel() {
        form.resetFields(["comment"]);
      },
    });
  };

  const showSalesCompletedForm = () => {
    return (
      <Form form={form} layout="vertical" className="mt-20">
        <div className="mb-10">
          <span>Add comments for partner:</span>
        </div>
        <Form.Item
          name="comment"
          className="mb-10"
          rules={[{ required: true, message: "Enter comments for partner" }]}
        >
          <TextArea placeholder="Enter comments here" />
        </Form.Item>
        <div className="mb-10">
          <span>Upload supporting documents:</span>
        </div>
        <Form.Item
          name="sales_completed_doc"
          valuePropName="fileList"
          getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
        >
          <Upload
            beforeUpload={(file) => {
              const isValidType = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'].includes(file.type);
              const isValidSize = file.size / 1024 / 1024 <= 15;

              if (!isValidType) {
                message.error('Please upload a PDF, JPEG, or PNG document!');
                return Upload.LIST_IGNORE;
              }
              if (!isValidSize) {
                message.error('File size must be less than or equal to 15 MB!');
                return Upload.LIST_IGNORE;
              }

              const files = form.getFieldValue('sales_completed_doc') || [];
              form.setFieldsValue({ sales_completed_doc: [...files, file] });
              return false;
            }}
            onRemove={(file) => {
              const files = form.getFieldValue('sales_completed_doc') || [];
              form.setFieldsValue({ 
                sales_completed_doc: files.filter(f => f.uid !== file.uid) 
              });
            }}
            accept=".pdf,.jpg,.jpeg,.png"
          >
            <Button icon={<UploadOutlined />}>Select File</Button>
          </Upload>
        </Form.Item>
      </Form>
    );
  };

  const showSalesCompletedConfirm = (item, partner_id) => {
    form.resetFields(['comment', 'sales_completed_doc']);

    confirm({
      title: "Do you want to mark sales as completed?",
      className: "btn-confirm-primary",
      content: showSalesCompletedForm(),
      icon: <CheckCircleOutlined />,
      okText: "Complete",
      cancelText: "Cancel",
      onOk(close) {
        form.validateFields().then(values => {
          const formData = new FormData();
          formData.append("is_sales_completed", true);
          formData.append("sales_completed_comment", values.comment);
          formData.append("partner_id", parseInt(partner_id));

          // Append files with category
          values.sales_completed_doc?.forEach(file => {
            const fileWithCategory = new File([file.originFileObj], file.name, { 
              type: file.type 
            });
            fileWithCategory.category = 'SALES_COMPLETED';
            formData.append("file", fileWithCategory);
          });

          editSalesRequest({
            id: item,
            data: formData,
            status: "Sales Completed",
          }).then(() => {
            if (refetchData) refetchData();
            onClose();
          });

          close();
        });
      },
      onCancel() {
        form.resetFields();
      },
    });
  };

  const rejectedDocs =
  requestData?.file?.filter((file) => file.category === "REJECTED") || [];

  const salesCompletedDocs =
  requestData?.file?.filter((file) => file.category === "SALES_COMPLETED") || [];

  const labelStyle = { width: "200px" };

  console.log("111", requestData)

  return (
    <Drawer
      title="Royalty Sales Request Information"
      placement="right"
      closable={true}
      onClose={onClose}
      open={visible}
      width="100%"
      className="royalty-sales-drawer"
      headerStyle={{ paddingBottom: 16 }}
      bodyStyle={{ paddingTop: 0, overflow: "hidden" }}
    >
      {isLoading ? (
        <div className="drawer-loading-container">
          <Spin tip="Loading..." />
        </div>
      ) : (
        <div className="drawer-outer-container" style={{ marginTop: "24px" }}>
          <div className="drawer-content-container">
            <Descriptions
              bordered
              column={1}
              style={{
                boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.1)",
                borderRadius: "10px",
              }}
            >
              <Descriptions.Item label="Company Name" style={labelStyle}>
                {requestData?.partner_id?.company_name || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Product Name" style={labelStyle}>
                {editMode.product ? (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Select
                      style={{ width: '80%' }}
                      value={selectedProduct}
                      onChange={(value) => saveField('product_id', value)}
                    >
                      {products.map((product) => (
                        <Option key={product.product_id} value={product.product_id}>
                          {product.product_name}
                        </Option>
                      ))}
                    </Select>
                    <Button
                      type="link"
                      icon={<CloseOutlined />}
                      onClick={() => setEditMode({...editMode, product: false})}
                    />
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <span>{requestData?.product_id?.product_name || 'N/A'}</span>
                    {requestData?.product_id?.category && (
                      <span style={{ color: '#1890ff', fontSize: '12px' }}>
                        ({requestData.product_id.category})
                      </span>
                    )}
                    {isEditable(requestData?.status) && (
                      <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => setEditMode({...editMode, product: true})}
                      />
                    )}
                  </div>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Country" style={labelStyle}>
                {editMode.country ? (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Select
                      style={{ width: '80%' }}
                      value={selectedCountry}
                      onChange={(value) => {
                        handleCountryChange(value);
                        saveField('country_id', value);
                      }}
                    >
                      {royalty_countries_product.map((country) => (
                        <Option key={country.country_id} value={country.country_id}>
                          {country.country_name}
                        </Option>
                      ))}
                    </Select>
                    <Button
                      type="link"
                      icon={<CloseOutlined />}
                      onClick={() => setEditMode({...editMode, country: false})}
                    />
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    {requestData?.country_id?.country_name || 'N/A'}
                    {isEditable(requestData?.status) && (
                      <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => setEditMode({...editMode, country: true})}
                      />
                    )}
                  </div>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Request Date" style={labelStyle}>
                {requestData?.request_date
                  ? moment(requestData.request_date).format("DD/MM/YYYY")
                  : "-"}
              </Descriptions.Item>
              <Descriptions.Item
                label="Expected Date of Sale"
                style={labelStyle}
              >
                {requestData?.expected_date_of_sale
                  ? moment(requestData.expected_date_of_sale).format(
                      "DD/MM/YYYY",
                    )
                  : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Pack Size" style={labelStyle}>
                {requestData?.pack_size}
              </Descriptions.Item>
              <Descriptions.Item label="Quantity" style={labelStyle}>
                {editMode.quantity ? (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Input
                      type="number"
                      style={{ width: '80%' }}
                      value={quantityValue}
                      onChange={(e) => setQuantityValue(e.target.value)}
                      onBlur={(e) => {
                        const value = e.target.value;
                        const inputValue = parseInt(value, 10);
                        if (!isNaN(inputValue) && inputValue >= 0 && inputValue <= 99000000) {
                          saveField('quantity', value);
                        } else {
                          message.error("Quantity must be between 0 and 99Mn");
                          setEditMode({...editMode, quantity: false});
                        }
                      }}
                    />
                    <Button
                      type="link"
                      icon={<CloseOutlined />}
                      onClick={() => setEditMode({...editMode, quantity: false})}
                    />
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    {requestData?.quantity}
                    {isEditable(requestData?.status) && (
                      <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => setEditMode({...editMode, quantity: true})}
                      />
                    )}
                  </div>
                )}
              </Descriptions.Item>
              <Descriptions.Item
                label="Proof of Sale Details"
                style={labelStyle}
              >
                {requestData?.proof_of_sale_details}
              </Descriptions.Item>
              <Descriptions.Item
                label="Buyer Name and Address"
                style={labelStyle}
              >
                {requestData?.buyer_name_and_address}
              </Descriptions.Item>
              <Descriptions.Item
                label="End Customer and Address"
                style={labelStyle}
              >
                {requestData?.end_customer_and_address}
              </Descriptions.Item>
              <Descriptions.Item label="Status" style={labelStyle}>
                {requestData?.status}
                {requestData?.clarification?.length > 0 && (
                  <Tooltip
                    onClick={() => {
                      handleClarificationClick(requestData.clarification);
                    }}
                  >
                    <span
                      style={{
                        cursor: "pointer",
                        paddingLeft: "5px",
                      }}
                    >
                      <MessageTwoTone
                        twoToneColor="#00AEEF"
                        className="font-20"
                      />
                    </span>
                  </Tooltip>
                )}
              </Descriptions.Item>
              {requestData?.comment && (
                <Descriptions.Item label="Comments" style={labelStyle}>
                  {requestData.comment}
                </Descriptions.Item>
              )}
              {requestData?.sales_completed_comment && (
                <Descriptions.Item label="Sales Completion Comment" style={labelStyle}>
                  {requestData.sales_completed_comment}
                </Descriptions.Item>
              )}

              {/* PO Documents */}
              {requestData?.file?.some(file => file.category === 'PR/PO' || !file.category) && (
                <Descriptions.Item label="Documents" style={labelStyle}>
                  {requestData.file
                    .filter(file => file.category === 'PR/PO' || !file.category)
                    .map((file, index) => (
                      <div key={file.po_id || index}>
                        <a
                          href={file.po_document}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          PO {index + 1}
                        </a>
                        <br />
                      </div>
                    ))}
                </Descriptions.Item>
              )}

              {/* Rejected Documents */}
              {rejectedDocs.length > 0 && (
                <Descriptions.Item label="Rejection Supporting Documents" style={labelStyle}>
                  {rejectedDocs.map((file, index) => (
                    <div key={file.po_id || index}>
                      <a
                        href={file.po_document}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        DOC {index + 1}
                      </a>
                      <br />
                    </div>
                  ))}
                </Descriptions.Item>
              )}

              {/* Sales Completed Documents */}
              {salesCompletedDocs.length > 0 && (
                <Descriptions.Item label="Sales Completion Supporting Documents" style={labelStyle}>
                  {salesCompletedDocs.map((file, index) => (
                    <div key={file.po_id || index}>
                      <a
                        href={file.po_document}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        DOC {index + 1}
                      </a>
                      <br />
                    </div>
                  ))}
                </Descriptions.Item>
              )}
            </Descriptions>

            <Modal
              title="Clarification Chat"
              visible={isChatModalVisible}
              onCancel={() => setIsChatModalVisible(false)}
              footer={null}
            >
              <div style={{ maxHeight: 400, overflowY: "auto" }}>
                <List
                  itemLayout="horizontal"
                  dataSource={chatConversation}
                  renderItem={(message) => (
                    <List.Item
                      className={classNames("chat-message", {
                        "chat-message-user": !message.is_partner,
                        "chat-message-partner": message.is_partner,
                      })}
                    >
                      <List.Item.Meta
                        description={
                          <div
                            style={{
                              background: message.is_partner
                                ? "#f6f6f6"
                                : "#e6f7ff",
                            }}
                            className="chat-message-content"
                          >
                            {message.message}
                          </div>
                        }
                      />
                    </List.Item>
                  )}
                />
              </div>
              {requestData?.status === "NEEDS CLARIFICATION" && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: 20,
                  }}
                >
                  <TextArea
                    rows={3}
                    value={messageInput}
                    onChange={handleMessageChange}
                    placeholder="Type your message..."
                    autoFocus
                    style={{ flex: 1, marginRight: 10 }}
                  />
                  <Button
                    type="primary"
                    onClick={() => {
                      handleSendMessage(chatConversation[0]?.request_id);
                    }}
                    loading={isEditing}
                  >
                    Send
                  </Button>
                </div>
              )}
            </Modal>
          </div>

          {isUserAdmin && requestData && (
            <div className="drawer-action-buttons-container">
              <Button
                className="gx-btn-success RSDApproveButton"
                icon={<CheckOutlined />}
                onClick={() =>
                  showApproveConfirm(
                    requestData.request_id,
                    requestData.partner_id?.partner_id,
                  )
                }
                id={`approve-${requestData.request_id}`}
                disabled={isDisabled}
                loading={isEditing}
              >
                Approve
              </Button>

              <Button
                className="gx-btn-danger RSDRejectButton"
                icon={<CloseOutlined />}
                onClick={() =>
                  showRejectConfirm(
                    requestData.request_id,
                    requestData.partner_id?.partner_id,
                  )
                }
                id={`reject-${requestData.request_id}`}
                disabled={!canReject()}
                loading={isEditing}
              >
                Reject
              </Button>

              <Button
                className="gx-btn-yellow RSDRevisionsButton"
                onClick={() =>
                  showReviseConfirm(
                    requestData.request_id,
                    requestData.partner_id?.partner_id,
                  )
                }
                icon={<ExclamationCircleOutlined />}
                id={`revisions-${requestData.request_id}`}
                disabled={isDisabled}
                loading={isEditing}
              >
                Revisions Required
              </Button>

              <Button
                className="gx-btn-warning RSDClarificationButton"
                onClick={() =>
                  showClarificationConfirm(
                    requestData.request_id,
                    requestData.partner_id?.partner_id,
                  )
                }
                icon={<WarningOutlined />}
                id={`clarification-${requestData.request_id}`}
                disabled={!isAdminApprover || requestData.status !== "PENDING"}
                loading={isEditing}
              >
                Needs Clarification
              </Button>

              <Button
                className="gx-btn-primary RSDSalesButton"
                icon={<CheckCircleOutlined />}
                onClick={() =>
                  showSalesCompletedConfirm(
                    requestData.request_id,
                    requestData.partner_id?.partner_id,
                  )
                }
                id={`sales-completed-${requestData.request_id}`}
                disabled={
                  requestData.status !== "APPROVED" ||
                  requestData.is_sales_completed === true ||
                  !(requestData.sales_data?.length > 0 &&
                    requestData.quantity &&
                    requestData.sales_data.reduce((total, item) => total + parseInt(item.quantity), 0) < requestData.quantity &&
                    requestData.sales_data.reduce((total, item) => total + parseInt(item.quantity), 0) !== requestData.quantity)
                }
                loading={isEditing}
              >
                Sales Completed
              </Button>
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
};

export default RoyaltySalesDrawer;
